import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Package, TrendingUp, Search, BarChart3, Loader2 } from "lucide-react";

interface WelcomeScreenProps {
  onLoadSampleData: () => Promise<void>;
  onStartFresh: () => Promise<void>;
  onSkip: () => void;
}

const WelcomeScreen = ({ onLoadSampleData, onStartFresh, onSkip }: WelcomeScreenProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");

  const handleLoadSampleData = async () => {
    setIsLoading(true);
    setLoadingMessage("Generating sample data...");
    try {
      await onLoadSampleData();
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  const handleStartFresh = async () => {
    setIsLoading(true);
    setLoadingMessage("Setting up your workspace...");
    try {
      await onStartFresh();
    } finally {
      setIsLoading(false);
      setLoadingMessage("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-6 py-12">
        <div className="max-w-4xl w-full">
          {/* Header */}
          <div className="text-center mb-12">
            <img
              src="/logo.png"
              alt="senloop"
              className="inline-block w-16 h-16 rounded-2xl mb-6"
            />
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Welcome to senloop
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Product feedback, organized for action.
            </p>
          </div>

          {/* Main Content */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12 mb-8">
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                Your workspace is empty
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                To help you explore the platform's capabilities, we can load sample data with realistic feedback or you can start from scratch.
              </p>
            </div>

            {/* Two Options */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Option 1: Sample Data */}
              <div className="border-2 border-border dark:border-gray-700 rounded-xl p-6 hover:border-primary dark:hover:border-gray-500 transition-colors">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 bg-secondary dark:bg-gray-700 rounded-lg">
                    <Package className="h-6 w-6 text-primary dark:text-gray-300" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Load Sample Data
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                      Perfect for exploring features immediately
                    </p>
                  </div>
                </div>
                
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-primary mt-0.5">✓</span>
                    <span><strong>3 demo products</strong> (Focus, Luxus, Protos)</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-primary mt-0.5">✓</span>
                    <span><strong>1,500 feedback entries</strong> with varied sentiments</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-primary mt-0.5">✓</span>
                    <span><strong>Realistic, varied content</strong> with tags, keywords, and summaries</span>
                  </li>
                </ul>

                <div className="bg-secondary/70 dark:bg-gray-700/50 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 font-medium">
                    This lets you immediately:
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3" />
                      <span>Explore sentiment analysis</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <BarChart3 className="h-3 w-3" />
                      <span>View analytics dashboards</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Search className="h-3 w-3" />
                      <span>Test filtering & search</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      <span>See insights & trends</span>
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  <strong>Easy cleanup:</strong> Just delete the demo products when you're ready!
                </p>

                <Button 
                  onClick={handleLoadSampleData}
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-primary via-[#E46C55] to-[#FF8A2A] hover:from-primary/90 hover:via-[#E46C55]/90 hover:to-[#FF8A2A]/90"
                  size="lg"
                >
                  {isLoading && loadingMessage === "Generating sample data..." ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Load Sample Data
                    </>
                  )}
                </Button>
              </div>

              {/* Option 2: Start Fresh */}
              <div className="border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <Package className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Start From Scratch
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                      Build your workspace from the ground up
                    </p>
                  </div>
                </div>
                
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400 mt-0.5">✓</span>
                    <span>Create your own products</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400 mt-0.5">✓</span>
                    <span>Set up custom feedback forms</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <span className="text-gray-400 mt-0.5">✓</span>
                    <span>Start collecting real feedback</span>
                  </li>
                </ul>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-16">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Perfect if you're ready to set up your actual products and start collecting real customer feedback right away.
                  </p>
                </div>

                <Button 
                  onClick={handleStartFresh}
                  disabled={isLoading}
                  variant="outline"
                  className="w-full"
                  size="lg"
                >
                  {isLoading && loadingMessage === "Setting up your workspace..." ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Setting up...
                    </>
                  ) : (
                    "Start Fresh"
                  )}
                </Button>
              </div>
            </div>

            {/* Skip Button */}
            <div className="text-center">
              <Button
                variant="ghost"
                onClick={onSkip}
                disabled={isLoading}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                Maybe Later
              </Button>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center text-sm text-gray-500 dark:text-gray-400">
            <p>You can always access the Products page to add or manage your products later.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;

